import React, { useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import SplashScreen from './components/TestComponent';
import Header from './components/shared/Header';
import Footer from './components/shared/Footer';
import { IntlProvider } from 'react-intl';
import messages_fr from './translations/locales/fr.json';
import messages_en from './translations/locales/en.json';
import { addLanguages } from './redux/actions/languagesActions';
import { connect } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import Units from './components/unit/Units';
import Users from './components/user/Users';
import MessageCenter from './components/messages/MessageCenter';
import LoaderBar from './components/ui/LoaderBar';
import Eula from './components/eula/Eula';
import {
  processAuth,
  getAuth,
  CLEAR_REFILL_STORE,
} from './redux/actions/authActions';
import Overlay from './components/shared/Overlay';
import { getCurrentUserInfo } from './redux/actions/currentUserActions';
import RedirectToMain from './components/eula/RedirectToMain';
import EulaView from './components/eula/EulaView';
import AlertMessageContainer from './components/ui/AlertMessageContainer';
import RedirectToLogin from './components/shared/RedirectToLogin';
import Devices from './components/devices/Devices';
import Reports from './components/reports/Reports';
import MyProfile from './components/myProfile/MyProfile';
import ScrollToTop from './components/shared/ScrollToTop';
import ExpiredPassword from './components/user/ExpiredPassword';
import Support from './components/support/Support';
//import { UnconnectedAssignDevices } from './components/installSetup/AssignDevicesToUnits';
import AssignDevices from './components/installSetup/AssignDevices';
import DocumentsAndImages from './components/administration/Administration';
import LoginRecovery from './components/LoginEntry/LoginPasswordUsername';
import PasswordResetEntry from './components/LoginEntry/ForgetPassword/PasswordResetEntry';
import ForgotUsername from './components/LoginEntry/ForgetUsername/UsernameEmailEntry';


/**
 * The primary component for the application
 * @description - This component sets up the primary router for displaying different routes throughout the site as well as wrapping the lower components in the IntlProvider component to allow for interface internationalization
 */

const PUBLIC_PATHS = ['/LoginEntry/forgetPassword', '/LoginEntry/forgetUsername', '/LoginEntry/resetPassword'];

function UnconnectedSaraContent(props) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // This is the list of languages translated for the site
  // and needs to be updated when new translations are added
  const languages = [
    { name: 'English', id: 'en' },
    { name: 'Français', id: 'fr' },
  ];

  const { auth, sessionToken, currentUser, selectedLanguage } = props;

  // This is where the language files of translations are added together
  // to be used by the internationalization system
  const messages = {
    fr: messages_fr,
    en: messages_en,
  };

  // This loads the list of languages into the Redux store
  props.dispatch(addLanguages(languages));

  const userRef = useRef();
  const passwordRef = useRef();

  let validToken = false;
  let validSessionToken = false;

  if (auth && window.token) {
    if (auth === window.token) {
      validToken = true;
    }
  } else if (auth) {
    validToken = true;
  }
  if (sessionToken && window.sessionToken) {
    if (sessionToken === window.sessionToken) {
      validSessionToken = true;
    } else {
      props.dispatch({
        type: CLEAR_REFILL_STORE,
      });
    }
  } else {
    validSessionToken = true;
  }
  useEffect(() => {
    // check to see if theres a token coming from PHP
    if (window.token) {
      // if there is, and there's a redux token ('auth')
      // AND if those two don't match
      if (auth && window.token && auth !== window.token) {
        // dispatch the action to clear the redux store to be
        // refilled
        props.dispatch({
          type: CLEAR_REFILL_STORE,
        });
      }
      // dispatch the action to add the window token to the
      // redux store
      props.dispatch(
        processAuth({ token: window.token, sessionToken: window.sessionToken })
      );
    } else {
      // dispatch the action to retrieve the currently logged in
      // user's info from the api
      props.dispatch(getCurrentUserInfo(navigate));
    }
  }, []); // eslint-disable-line

  function authSubmit(event) {
    event.preventDefault();
    props.dispatch(getAuth(userRef.current.value, passwordRef.current.value));
  }

  function displayLogin() {
    const loginForm = (
      <Overlay canClose={false}>
        <div
          data-test="login-form"
          className="login-form content-container content-container--column">
          <form
            id="loginForm"
            className="form"
            role="search"
            onSubmit={authSubmit}>
            <label htmlFor="login_name" className="formLabel">
              Username
            </label>
            <input
              type="text"
              id="login_name"
              className="formField"
              required
              ref={userRef}
            />
            <label htmlFor="login_pw" className="formLabel">
              Password
            </label>
            <input
              type="password"
              name="password"
              id="login_pw"
              className="formField"
              required
              ref={passwordRef}
            />
            <button className="button" type="submit">
              Log In
            </button>
          </form>
        </div>
      </Overlay>
    );

    const isPublicPath = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p));
    if (!window.token && !auth && !isPublicPath) {
      return loginForm;
    } else {
      return '';
    }
  }

  // This fetches the current state of the selectedLanguage in the Redux store.
  // This value is used for the IntlProvider component to determine the value of the
  // text being displayed in the interface
  const currentLanguage = selectedLanguage;

  // This array defines the routes for react-router. By defining them here,
  // they are referenced multiple times below and define what header and primary component to display
  const routes = [
    {
      path: '/',
      header: () => <Header />,
      main: () => <SplashScreen />,
      footer: () => <Footer />,
    },
    {
      path: '/administration/*',
      header: () => <Header />,
      main: () => <DocumentsAndImages />,
      footer: () => <Footer />,
    },
    {
      path: '/users/*',
      header: () => <Header />,
      main: () => <Users />,
      footer: () => <Footer />,
    },
    {
      path: '/units/*',
      header: () => <Header />,
      main: () => <Units />,
      footer: () => <Footer />,
    },
    {
      path: '/devices/*',
      header: () => <Header />,
      main: () => <Devices />,
      footer: () => <Footer />,
    },
    {
      path: '/messaging/*',
      header: () => <Header />,
      main: () => <MessageCenter />,
      footer: () => <Footer />,
    },
    {
      path: '/reports/*',
      header: () => <Header />,
      main: () => <Reports />,
      footer: () => <Footer />,
    },
    {
      path: '/installsetup/assigndevices/*',
      header: () => <Header />,
      main: () => <AssignDevices />,
      footer: () => <Footer />,
    },
    {
      path: '/myProfile/*',
      header: () => <Header />,
      main: () => <MyProfile />,
      footer: () => <Footer />,
    },
    {
      path: '/support/*',
      header: () => <Header />,
      main: () => <Support />,
      footer: () => <Footer />,
    },
    {
      path: '/eulaView',
      header: () => <Header />,
      main: () => <EulaView />,
      footer: () => <Footer />,
    },
    {
      path: '/eula',
      header: () => '',
      main: () => <Eula />,
      footer: () => '',
    },
    {
      path: '/passwordExpired',
      header: () => '',
      main: () => <ExpiredPassword />,
      footer: () => '',
    },
    {
      path: '/login/*',
      header: () => '',
      main: () => <LoginRecovery />,
      footer: () => '',
    },
  ];

  // Component wrapper to handle header/main/footer for each route
  const RouteWrapper = ({ route }) => (
    <React.Fragment>
      {route.header()}
      <section id="body" aria-label="Main Content" data-test="main-content">
        {route.main()}
      </section>
      {route.footer()}
    </React.Fragment>
  );

  const today = new Date();
  let passwordExpire = new Date(currentUser.password_expires);
  if (currentUser.password_expires === null) {
    passwordExpire = new Date();
  }

  return (
    <IntlProvider locale={currentLanguage} messages={messages[currentLanguage]}>
      <HelmetProvider>
        <ScrollToTop />
        <React.Fragment>
          <Routes>
            <Route path="/logout" element={<RedirectToLogin />} />
            <Route path="/LoginEntry/*" element={<LoginRecovery />} />
            <Route path="/resetPassword" element={<PasswordResetEntry />} />
            <Route path="/forgetUsername" element={<ForgotUsername />} />
          </Routes>
          <LoaderBar />
          <AlertMessageContainer />
          {displayLogin()}
          {/* This checks to see if the user has an auth token and if their user info has loaded by checking to see if their username exists. If they are logged in, the interface will load. Otherwise, nothing is displayed. */}
          {validToken && validSessionToken && auth && currentUser.username ? (
            passwordExpire && passwordExpire < today ? (
              <Routes>
                <Route
                  data-test="password-expired"
                  path="/passwordExpired"
                  element={<ExpiredPassword />}
                />
                <Route path="*" element={<Navigate to="/logout" replace />} />
              </Routes>
            ) : (
              <Routes>
                {routes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={<RouteWrapper route={route} />}
                  />
                ))}
                <Route 
                  path="/redirectToMain"
                  element={<RedirectToMain />}
                />
              </Routes>
            )
          ) : (
            ''
          )}
        </React.Fragment>
      </HelmetProvider>
    </IntlProvider>
  );
}

const mapStateToProps = (state) => {
  return {
    selectedLanguage: state.multilingual.selectedLanguage,
    auth: state.auth.token,
    sessionToken: state.auth.sessionToken,
    currentUser: state.currentUser,
  };
};

const ConnectedSaraContent = connect(mapStateToProps)(UnconnectedSaraContent);

export function UnconnectedSara() {
  return (
    <Router basename={'/r'}>
      <ConnectedSaraContent />
    </Router>
  );
}

// Export for testing (without Router wrapper)
export { ConnectedSaraContent };

export default UnconnectedSara;
